import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { Button } from "../components/ui/button";
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { useConsultationModal } from '../context/ConsultationModalContext';

// Futuristic AI-style SVG Icons
const IconCompute = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 3V6M12 18V21M3 12H6M18 12H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
    </svg>
);

const IconNetwork = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="12" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="5" cy="19" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="19" cy="19" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 7.5V12M12 12L5.5 16.5M12 12L18.5 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
    </svg>
);

const IconStorage = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <ellipse cx="12" cy="6" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M4 6V12C4 13.657 7.582 15 12 15C16.418 15 20 13.657 20 12V6" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M4 12V18C4 19.657 7.582 21 12 21C16.418 21 20 19.657 20 18V12" stroke="currentColor" strokeWidth="1.5"/>
        <ellipse cx="12" cy="12" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/>
    </svg>
);

const IconGPU = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 10H8V14H6V10Z" fill="currentColor"/>
        <path d="M10 10H12V14H10V10Z" fill="currentColor"/>
        <path d="M14 10H16V14H14V10Z" fill="currentColor"/>
        <path d="M5 6V4M9 6V4M15 6V4M19 6V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="18" cy="12" r="1" fill="currentColor"/>
    </svg>
);

const IconMetal = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 9H21" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 14H21" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="6" cy="6.5" r="1" fill="currentColor"/>
        <circle cx="6" cy="11.5" r="1" fill="currentColor"/>
        <circle cx="6" cy="16.5" r="1" fill="currentColor"/>
        <path d="M9 6.5H18M9 11.5H18M9 16.5H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconKubernetes = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M12 2L21 7V17L12 22L3 17V7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 9V6M12 18V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9.5 10.5L7 8M17 16L14.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M14.5 10.5L17 8M7 16L9.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconDatabase = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <ellipse cx="12" cy="5" rx="7" ry="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 5V19C5 20.657 8.134 22 12 22C15.866 22 19 20.657 19 19V5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 10C5 11.657 8.134 13 12 13C15.866 13 19 11.657 19 10" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 14.5C5 16.157 8.134 17.5 12 17.5C15.866 17.5 19 16.157 19 14.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
);

const IconBackup = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M12 3V8L15 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M8 14H16M8 17H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconShield = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M12 3L4 7V12C4 16.418 7.582 20.163 12 21C16.418 20.163 20 16.418 20 12V7L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const IconInsight = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7 14L10 11L13 13L17 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="17" cy="9" r="1.5" fill="currentColor"/>
    </svg>
);

const Products = () => {
    const [activeSection, setActiveSection] = useState('infrastructure');
    const { openModal } = useConsultationModal();
    const location = useLocation();

    // Handle hash navigation from footer links
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (hash && ['infrastructure', 'performance', 'platforms', 'protection', 'security', 'operations'].includes(hash)) {
            setActiveSection(hash);
        }
    }, [location.hash]);

    const sections = [
        { id: 'infrastructure', label: 'Core Infrastructure', Icon: IconCompute },
        { id: 'performance', label: 'Performance Platforms', Icon: IconGPU },
        { id: 'platforms', label: 'Cloud Platforms', Icon: IconKubernetes },
        { id: 'protection', label: 'Data Protection', Icon: IconBackup },
        { id: 'security', label: 'Security & Identity', Icon: IconShield },
        { id: 'operations', label: 'Operations', Icon: IconInsight },
    ];

    return (
        <Layout>
            <SEO page="products" />
            <PageHeader
                title="EverestCloud" 
                highlight="Products"
                description="EverestCloud delivers a comprehensive portfolio of enterprise-grade cloud products designed to meet the performance, security, compliance, and operational requirements of banks, regulated enterprises, and government institutions."
            />

            <section className="py-16 bg-white dark:bg-slate-950 transition-colors duration-500">
                <div className="container mx-auto px-4">

                    {/* Section Navigation */}
                    <div className="flex flex-wrap justify-center gap-2 mb-12">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                                    activeSection === section.id
                                        ? 'bg-accent text-black shadow-lg'
                                        : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                            >
                                <div className="w-4 h-4">
                                    <section.Icon />
                                </div>
                                {section.label}
                            </button>
                        ))}
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-8">
                        
                        {/* Core Infrastructure */}
                        {activeSection === 'infrastructure' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ProductCard
                                    Icon={IconCompute}
                                    title="EverestCloud Compute"
                                    subtitle="Enterprise Infrastructure as a Service (IaaS)"
                                    description="EverestCloud Compute provides secure, high-availability virtual compute resources with predictable performance and transparent allocation."
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Dedicated or shared compute clusters",
                                        "Enterprise-grade hypervisor",
                                        "Guaranteed CPU & RAM allocation",
                                        "Policy-driven workload placement",
                                        "Live migration and self-healing infrastructure"
                                    ]}
                                    rightTitle="Designed For"
                                    rightTags={["Core banking systems", "Mobile Banking Application", "Enterprise ERP and middleware", "Mission-critical applications"]}
                                />

                                <ProductCard
                                    Icon={IconNetwork}
                                    title="EverestCloud Network"
                                    subtitle="Software-Defined Networking for Virtual Data Centers"
                                    description="EverestCloud Network delivers a fully software-defined networking layer enabling customers to design, isolate, and control their virtual data centers with fine-grained security and visibility."
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Virtual Private Cloud (VPC) per tenant",
                                        "Public & private subnet isolation",
                                        "Managed Load Balancers (L4 / L7)",
                                        "IPsec VPN Gateway (site-to-site & client VPN)",
                                        "Controlled routing, segmentation, and access policies"
                                    ]}
                                    rightTitle="Benefits"
                                    rightItems={[
                                        "Complete tenant isolation",
                                        "Bank-controlled IP addressing",
                                        "Zero-trust ready network architecture"
                                    ]}
                                />

                                <ProductCard
                                    Icon={IconStorage}
                                    title="EverestCloud Storage Services"
                                    subtitle="Enterprise Storage as a Service"
                                    leftTitle="Object Storage"
                                    leftItems={[
                                        "API-compatible object storage",
                                        "Immutability and versioning",
                                        "Encryption at rest and in transit"
                                    ]}
                                    rightTitle="Block & File Storage"
                                    rightItems={[
                                        "NVMe-backed performance",
                                        "Low-latency volumes",
                                        "Snapshot and replication support",
                                        "Shared enterprise file services"
                                    ]}
                                />
                            </div>
                        )}

                        {/* Performance & Specialized Platforms */}
                        {activeSection === 'performance' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ProductCard
                                    Icon={IconGPU}
                                    title="EverestCloud GPU Platforms"
                                    subtitle="Accelerated Computing for AI & HPC Workloads"
                                    leftTitle="Basecamp GPU Cluster"
                                    leftItems={[
                                        "Entry-level GPU platform",
                                        "Secure GPU virtualization",
                                        "AI inference and analytics",
                                        "Secure virtual desktop workloads"
                                    ]}
                                    rightTitle="Summit GPU Cluster"
                                    rightItems={[
                                        "High-performance AI & HPC platform",
                                        "Large-memory GPU acceleration",
                                        "AI/ML training and inference",
                                        "HPC, simulation, and rendering"
                                    ]}
                                />

                                <ProductCard
                                    Icon={IconMetal}
                                    title="EverestCloud Metal"
                                    subtitle="Dedicated Bare-Metal Infrastructure"
                                    description="EverestCloud Metal provides single-tenant physical servers for workloads requiring maximum performance, direct hardware access, and strict licensing or regulatory compliance."
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Single-tenant physical servers",
                                        "No hypervisor overhead",
                                        "Direct CPU, memory, and device access",
                                        "Custom firmware and BIOS configuration"
                                    ]}
                                    rightTitle="Designed For"
                                    rightTags={["Licensed enterprise databases", "Latency-sensitive workloads", "Compliance-required workloads"]}
                                />
                            </div>
                        )}

                        {/* Cloud Platforms & Data Services */}
                        {activeSection === 'platforms' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ProductCard
                                    Icon={IconKubernetes}
                                    title="EverestCloud Kubernetes Platform"
                                    subtitle="Enterprise Container Platform"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Managed Kubernetes service",
                                        "Multi-cluster orchestration",
                                        "Role-based access control",
                                        "Secure networking and storage integration"
                                    ]}
                                />

                                <ProductCard
                                    Icon={IconDatabase}
                                    title="EverestCloud Database Services (DBaaS)"
                                    subtitle="Managed Database Platform"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Enterprise-ready database platforms",
                                        "Backup and point-in-time recovery",
                                        "Secure tenant isolation",
                                        "Performance monitoring and scaling"
                                    ]}
                                />
                            </div>
                        )}

                        {/* Data Protection & Resilience */}
                        {activeSection === 'protection' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ProductCard
                                    Icon={IconBackup}
                                    title="EverestCloud Backup Services"
                                    subtitle="Enterprise Backup Solution"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Image-level and application-aware backups",
                                        "Encryption at rest and in transit",
                                        "Self-service restore capabilities",
                                        "Policy-based retention management"
                                    ]}
                                />

                                <ProductCard
                                    Icon={IconBackup}
                                    title="EverestCloud Disaster Recovery (DRaaS)"
                                    subtitle="Business Continuity Solution"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Synchronous, near-synchronous, and asynchronous replication",
                                        "Defined RPO and RTO commitments",
                                        "Pre-provisioned or on-demand recovery environments",
                                        "Controlled failover and failback operations"
                                    ]}
                                />
                            </div>
                        )}

                        {/* Security & Identity */}
                        {activeSection === 'security' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ProductCard
                                    Icon={IconShield}
                                    title="EverestCloud Vault"
                                    subtitle="Privileged Access Management & MFA"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Secure credential vaulting",
                                        "Multi-factor authentication",
                                        "Session recording and audit trails",
                                        "Just-in-time privileged access"
                                    ]}
                                />

                                <ProductCard
                                    Icon={IconShield}
                                    title="EverestCloud Shield"
                                    subtitle="Network & Workload Protection"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Next-generation firewall capabilities",
                                        "Intrusion prevention and malware protection",
                                        "East–west traffic segmentation",
                                        "DDoS mitigation"
                                    ]}
                                />

                                <ProductCard
                                    Icon={IconShield}
                                    title="EverestCloud WAF"
                                    subtitle="Application-Layer Protection"
                                    description="EverestCloud WAF protects web applications and APIs from application-layer attacks and exploits."
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "OWASP Top 10 protection",
                                        "SQL injection and XSS prevention",
                                        "API security enforcement",
                                        "Rate limiting and bot mitigation",
                                        "SSL/TLS inspection and termination"
                                    ]}
                                    rightTitle="Designed For"
                                    rightTags={["Internet-facing banking applications", "Digital channels and portals", "APIs and middleware services"]}
                                />
                            </div>
                        )}

                        {/* Operations & Visibility */}
                        {activeSection === 'operations' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ProductCard
                                    Icon={IconInsight}
                                    title="EverestCloud Insight"
                                    subtitle="Monitoring & Observability"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Infrastructure and service monitoring",
                                        "Performance dashboards",
                                        "Threshold-based alerting"
                                    ]}
                                />

                                <ProductCard
                                    Icon={IconInsight}
                                    title="EverestCloud Pulse"
                                    subtitle="Availability & Service Health"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Uptime visibility",
                                        "SLA tracking",
                                        "Incident awareness"
                                    ]}
                                />

                                <ProductCard
                                    Icon={IconInsight}
                                    title="EverestCloud Atlas"
                                    subtitle="IPAM & DCIM"
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "IP address management",
                                        "Network documentation",
                                        "Infrastructure lifecycle visibility"
                                    ]}
                                />
                            </div>
                        )}

                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-slate-50 dark:bg-slate-900/50 transition-colors duration-500">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                        Ready to Deploy on Nepal&apos;s Sovereign Cloud?
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
                        Contact our solutions team to discuss your infrastructure requirements and get a customized proposal.
                    </p>
                    <Button 
                        className="h-14 px-10 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-full shadow-lg"
                        onClick={() => openModal('general')}
                    >
                        Request a Consultation
                        <ChevronRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>
        </Layout>
    );
};

// Unified Product Card Component
const ProductCard = ({ Icon, title, subtitle, description, leftTitle, leftItems, leftTags, rightTitle, rightItems, rightTags }) => (
    <div className="p-8 rounded-2xl bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800">
        <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl shrink-0 w-12 h-12 bg-accent/10 text-accent">
                <Icon />
            </div>
            <div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{title}</h3>
                <p className="text-accent font-medium">{subtitle}</p>
            </div>
        </div>
        
        {description && (
            <p className="text-slate-600 dark:text-slate-300 mb-8 text-lg leading-relaxed max-w-4xl">
                {description}
            </p>
        )}

        <div className={`grid ${rightTitle ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-8`}>
            {/* Left Column */}
            {(leftItems || leftTags) && (
                <div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">{leftTitle}</h4>
                    {leftItems && (
                        <ul className="space-y-3">
                            {leftItems.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                                    <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5 text-accent" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {leftTags && (
                        <div className="flex flex-wrap gap-2">
                            {leftTags.map((item, i) => (
                                <span key={i} className="px-3 py-1.5 rounded-full text-sm font-medium bg-accent/10 text-accent">
                                    {item}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Right Column */}
            {(rightItems || rightTags) && (
                <div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">{rightTitle}</h4>
                    {rightItems && (
                        <ul className="space-y-3">
                            {rightItems.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                                    <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5 text-accent" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {rightTags && (
                        <div className="flex flex-wrap gap-2">
                            {rightTags.map((item, i) => (
                                <span key={i} className="px-3 py-1.5 rounded-full text-sm font-medium bg-accent/10 text-accent">
                                    {item}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    </div>
);

export default Products;
